/**
 * TanStack Query hooks for `advertiser-billing`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	remove,
	listInvoices,
	generateInvoice,
	advertiserBillingQueryKeys,
	type CreateAdvertiserBillingBody,
	type GenerateInvoiceBody,
	type ListAdvertiserBillingResponse,
	type ListInvoicesResponse,
	type AdvertiserBillingRow,
	type InvoiceRow,
} from "./api";

export function useAdvertiserBillingList(): UseQueryResult<ListAdvertiserBillingResponse, Error> {
	return useQuery({
		queryKey: advertiserBillingQueryKeys.list(),
		queryFn: list,
	});
}

export function useAdvertiserBillingCreate(): UseMutationResult<AdvertiserBillingRow, Error, CreateAdvertiserBillingBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserBillingQueryKeys.all });
		},
	});
}

export function useAdvertiserBillingDelete(): UseMutationResult<{ removed: boolean }, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserBillingQueryKeys.all });
		},
	});
}

export function useAdvertiserInvoices(
	advertiserId: string | null,
): UseQueryResult<ListInvoicesResponse, Error> {
	return useQuery({
		queryKey: advertiserBillingQueryKeys.invoices(advertiserId ?? ""),
		queryFn: () => listInvoices(advertiserId as string),
		enabled: typeof advertiserId === "string" && advertiserId.length > 0,
	});
}

export function useGenerateInvoice(): UseMutationResult<
	InvoiceRow,
	Error,
	{ advertiserId: string; body?: GenerateInvoiceBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ advertiserId, body }) => generateInvoice(advertiserId, body),
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: advertiserBillingQueryKeys.all });
			qc.invalidateQueries({
				queryKey: advertiserBillingQueryKeys.invoices(vars.advertiserId),
			});
		},
	});
}
